import * as Cesium from 'cesium';
import { twoline2satrec, type SatRec } from 'satellite.js';
import { computeGroundTrack } from '../passes/computeGroundTrack';
import {
  DECAY_HALF_WINDOW_MS,
  DECAY_HUE,
  DECAY_SAMPLES_PER_TRACE,
  DECAY_SEGMENT_COUNT,
  decayTraceAlpha,
  validateTraces,
  type DecayTraceRecord,
} from './decayTraceMath';

/**
 * Phase 9 chunk 4 — DecayTraceLayer.
 *
 * Renders one fading-red polyline per `reentries` record in the
 * server payload. Each polyline traces the satellite's predicted
 * sub-satellite point over the 24 h preceding the TIP-message-
 * predicted decay (computed client-side via satellite.js so the
 * server doesn't need a Node-SGP4 round-trip per request).
 *
 * Wiring per docs/phase9.md § III chunk 4:
 *   - `setVisible(boolean)` toggles the whole layer; the polyline
 *     collection is cleared on hide
 *   - `setTraces(payload)` validates + caps + repaints
 *   - Polylines carry their NORAD as the Cesium primitive `id` so
 *     the chunk-4 click handler in {@link Globe.SelectionController}
 *     can route a `decay-pick` event without an extra lookup
 *
 * Click routing is owned by Globe.ts (the SelectionController
 * recognizes Cesium picks by primitive `id` and dispatches); this
 * layer just stamps the `id` correctly.
 */
export class DecayTraceLayer {
  private readonly collection: Cesium.PolylineCollection;
  private records: DecayTraceRecord[] = [];
  private visible = false;

  constructor(private readonly scene: Cesium.Scene) {
    this.collection = scene.primitives.add(new Cesium.PolylineCollection());
  }

  /**
   * Show or hide the entire layer. Clears the polyline collection on
   * hide so it doesn't keep stale traces around; subsequent show
   * calls re-render from the cached `records`.
   */
  setVisible(visible: boolean): void {
    if (visible === this.visible) return;
    this.visible = visible;
    if (visible) {
      this.render();
    } else {
      this.collection.removeAll();
    }
  }

  /** Replace the rendered set; re-renders if currently visible. */
  setTraces(payload: unknown): void {
    this.records = validateTraces(payload);
    if (this.visible) this.render();
  }

  /** Read-only snapshot for tests + debug overlays. */
  getRecords(): readonly DecayTraceRecord[] {
    return this.records;
  }

  destroy(): void {
    if (!this.scene.isDestroyed()) {
      this.scene.primitives.remove(this.collection);
    }
  }

  // ─── Internals ─────────────────────────────────────────────────────────

  private render(): void {
    this.collection.removeAll();
    for (const record of this.records) {
      const satrec = this.buildSatrec(record);
      if (satrec === null) continue;
      this.renderOne(record, satrec);
    }
  }

  private buildSatrec(record: DecayTraceRecord): SatRec | null {
    try {
      const satrec = twoline2satrec(record.line1, record.line2);
      if (satrec.error !== 0) return null;
      return satrec;
    } catch {
      return null;
    }
  }

  private renderOne(record: DecayTraceRecord, satrec: SatRec): void {
    const decayMs = Date.parse(record.predicted_decay);
    if (!Number.isFinite(decayMs)) return;
    // Center the ground-track sampler at the decay time, then ask for
    // ONE half-window's worth on each side. Forward-of-decay alpha is
    // floored by decayTraceAlpha (TIP messages don't predict post-decay),
    // so the user sees a one-sided fade toward the decay event.
    const centerTimeMs = decayMs;
    const pastOrbits   = DECAY_HALF_WINDOW_MS / (90 * 60_000); // ~8 orbits for a 90-min period
    const futureOrbits = 0;                                     // no forward arc — see comment above

    const points = computeGroundTrack({
      satrec,
      centerTimeMs,
      pastOrbits,
      futureOrbits,
      samplesPerOrbit: Math.ceil(DECAY_SAMPLES_PER_TRACE / Math.max(0.1, pastOrbits)),
    });
    if (points.length < 2) return;

    const segmentCount = Math.min(DECAY_SEGMENT_COUNT, Math.floor(points.length / 2));
    const samplesPerSegment = Math.ceil(points.length / segmentCount);

    for (let s = 0; s < segmentCount; s++) {
      const from = s * samplesPerSegment;
      const to   = Math.min((s + 1) * samplesPerSegment + 1, points.length);
      if (to - from < 2) continue;

      const slice = points.slice(from, to);
      const positions: Cesium.Cartesian3[] = [];
      for (const p of slice) {
        if (Number.isFinite(p.longitudeDeg) && Number.isFinite(p.latitudeDeg)) {
          positions.push(Cesium.Cartesian3.fromDegrees(p.longitudeDeg, p.latitudeDeg, 0));
        }
      }
      if (positions.length < 2) continue;

      const segmentCenterTimeMs = (slice[0].timeMs + slice[slice.length - 1].timeMs) / 2;
      const alpha = decayTraceAlpha(segmentCenterTimeMs - decayMs);
      const color = Cesium.Color.fromCssColorString(DECAY_HUE).withAlpha(alpha);
      this.collection.add({
        positions,
        width: 2,
        material: Cesium.Material.fromType('Color', { color }),
        // Stamp the polyline with a structured id the picker can
        // recognize → emits a `decay-pick` for the click handler.
        id: { kind: 'decay-trace', norad: record.norad_id, name: record.name },
      });
    }
  }
}
